// "Rest until your heart rate drops below 110" — as a suggestion layered on the countdown, never
// as a gate on it.
//
// The reason it can only ever be a suggestion is upstream: Mi Fitness writes into Health Connect
// at roughly one-minute granularity, so a reading can be most of a minute old by the time the page
// sees it. A timer that refused to finish until a stale number came down would strand the user
// mid-workout for reasons they cannot see. So the countdown is untouched, and this only ever adds
// a line saying you look ready.
//
// Everything here is pure so it can be tested against the awkward cases (stale samples, a lifter
// whose heart rate never went up) rather than only the happy one.

import { maxHeartRate } from "./health.js";

const KEY = "wt_rest_hr"; // per-device: the band pairs with one phone, not with an account

export function restHrEnabled() {
  try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
}
export function setRestHrEnabled(on) {
  try { localStorage.setItem(KEY, on ? "1" : "0"); } catch {}
}

// A reading older than this says nothing about now. Generous, because the band's own write
// cadence is ~60s — but well short of a whole rest, so a stale feed goes quiet rather than
// confidently reporting a heart rate from two sets ago.
export const STALE_MS = 150_000;
// How far above the threshold the effort must have taken you for "back down" to be news.
const MEANINGFUL_RISE = 12;
// Look back far enough to catch the peak of the set itself, not just the rest after it.
export const PEAK_WINDOW_MS = 300_000;

/**
 * The bpm at or below which a rest counts as recovered. ~65% of the Tanaka-estimated max is the
 * top of zone 1 — back to conversational. Without a birth year there's no estimate to work from,
 * so it falls back to the flat 110 the idea started from, which is defensible for most adults.
 */
export function recoveryThreshold(age) {
  const hrMax = maxHeartRate(age);
  if (!hrMax) return 110;
  return Math.max(95, Math.round(hrMax * 0.65));
}

/**
 * @param samples [{bpm, at}] newest last, as delivered by the live feed
 * @returns null when there's nothing honest to say, else {bpm, at, ageMs, threshold, peak, recovered}
 */
export function recoveryState(samples, { threshold, now = Date.now() } = {}) {
  if (!Array.isArray(samples) || !samples.length || !Number.isFinite(threshold)) return null;
  const latest = samples[samples.length - 1];
  if (!latest || !Number.isFinite(latest.bpm) || !Number.isFinite(latest.at)) return null;

  const ageMs = now - latest.at;
  if (ageMs > STALE_MS || ageMs < -STALE_MS) return null; // nothing current enough to claim

  const peak = samples.reduce((m, s) => (now - s.at <= PEAK_WINDOW_MS ? Math.max(m, s.bpm) : m), 0);
  // A lifter whose heart rate sat at 78 all session was never "recovering", and telling them
  // they're recovered is noise dressed as insight.
  if (peak < threshold + MEANINGFUL_RISE) return null;

  return { bpm: latest.bpm, at: latest.at, ageMs, threshold, peak, recovered: latest.bpm <= threshold };
}

/**
 * The one line shown under the countdown, or null. Deliberately phrased as an observation the
 * user can act on or ignore — and it carries how old the reading is, because a bare number
 * implies "now" and this one often isn't.
 */
export function recoveryMessage(state) {
  if (!state?.recovered) return null;
  const secs = Math.max(0, Math.round(state.ageMs / 1000));
  const when = secs < 20 ? "just now" : secs < 90 ? "a moment ago" : `${Math.round(secs / 60)} min ago`;
  return `HR ${state.bpm} ${when} — ready when you are`;
}
